import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem, 
  Stepper,
  Step,
  StepLabel, 
  Alert,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  SelectChangeEvent,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  LocalOffer,
  Business,
  Schedule,
  Info,
  Percent,
  AttachMoney,
  LocalActivity,
  MoneyOff,
  Preview,
  Publish,
  CloudUpload,
  Delete,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, addDoc, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { VenueData } from '../../types/VenueAdminTypes';

/// Returns YYYY-MM-DD for the LOCAL date, not UTC.
/// toISOString() converts to UTC first which rolls the date back
/// for any timezone ahead of UTC (e.g. AEST/AEDT) during early hours.
const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface OfferFormData {
  title: string;
  description: string;
  discountType: 'percentage' | 'fixed' | 'bogo' | 'free' | 'promotion';
  discountValue: number | string;
  originalPrice?: number;
  validFrom: string;
  validUntil: string;
  terms: string;
  venueId: string;
  maxRedemptions?: number;
  maxRedemptionsPerUser?: number;  
  isUnlimited: boolean;
  isUnlimitedPerUser: boolean;      
}

interface CreateOfferProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

const CreateOffer: React.FC<CreateOfferProps> = ({ onClose, onSuccess }) => {
  const { venueAdminData } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [venues, setVenues] = useState<VenueData[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<VenueData | null>(null);

  // Image upload states
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);

  // Form state
  const [formData, setFormData] = useState<OfferFormData>({
  title: '',
  description: '',
  discountType: 'percentage',
  discountValue: 0,
  originalPrice: undefined,
  validFrom: toLocalDateString(new Date()),
  validUntil: toLocalDateString(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
  terms: '',
  venueId: '',
  maxRedemptions: undefined,
  maxRedemptionsPerUser: undefined,  
  isUnlimited: true,
  isUnlimitedPerUser: true,          
});

  const steps = ['Offer Details', 'Venue & Schedule', 'Terms & Limits', 'Preview & Publish'];

  // Load venues
  useEffect(() => {
    loadVenues();
  }, [venueAdminData]);

  const loadVenues = async () => {
    if (!venueAdminData?.adminId) return;

    try {
      const venuesQuery = query(
        collection(db, 'venues'),
        where('ownerId', '==', venueAdminData.adminId)
      );
      const venuesSnapshot = await getDocs(venuesQuery);
      const venuesData = venuesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as VenueData));

      setVenues(venuesData);
      
      if (venuesData.length === 1) {
        setSelectedVenue(venuesData[0]);
        setFormData(prev => ({ ...prev, venueId: venuesData[0].id }));
      }
    } catch (error) {
      console.error('Error loading venues:', error);
      setError('Failed to load venues');
    }
  };

  // Image handling functions
  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image must be less than 5MB');
      return;
    }

    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    
    setError('');
  }, []);

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
  }, []);

  const uploadOfferImage = async (offerId: string, venueId: string, imageFile: File): Promise<{ imageUrl: string; imagePath: string }> => {
    try {
      const imagePath = `offers/${venueId}/${offerId}/image.${imageFile.name.split('.').pop()}`;
      const storageRef = ref(storage, imagePath);
      
      const metadata = {
        contentType: imageFile.type,
        customMetadata: {
          offerId: offerId,
          venueId: venueId,
          uploadedAt: new Date().toISOString(),
        }
      };
      
      await uploadBytes(storageRef, imageFile, metadata);
      const downloadURL = await getDownloadURL(storageRef);
      
      return { imageUrl: downloadURL, imagePath };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  };

  const handleInputChange = useCallback((field: keyof OfferFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDiscountTypeChange = useCallback((newType: string) => {
    let newDiscountValue: number | string;
    
    if (newType === 'percentage' || newType === 'fixed') {
      newDiscountValue = 0;
    } else {
      newDiscountValue = '';
    }
    
    setFormData(prev => ({ 
      ...prev, 
      discountType: newType as 'percentage' | 'fixed' | 'bogo' | 'free' | 'promotion',
      discountValue: newDiscountValue
    }));
  }, []);

  const handleVenueChange = useCallback((venueId: string) => {
    const venue = venues.find(v => v.id === venueId);
    setSelectedVenue(venue || null);
    setFormData(prev => ({ ...prev, venueId }));
  }, [venues]);

  const validateStep = useCallback((step: number): boolean => {
    switch (step) {
      case 0:
        return !!(
          formData.title.trim() &&
          formData.description.trim() &&
          (
            ((formData.discountType === 'percentage' || formData.discountType === 'fixed') && 
             typeof formData.discountValue === 'number' && 
             formData.discountValue > 0) ||
            ((formData.discountType === 'bogo' || formData.discountType === 'free') && 
             typeof formData.discountValue === 'string' && 
             formData.discountValue.trim().length > 0) ||
            formData.discountType === 'promotion' // no discount value required
          ) &&
          (formData.discountType !== 'fixed' || formData.originalPrice)
        );
      case 1:
        return !!(
          formData.venueId &&
          formData.validFrom &&
          formData.validUntil &&
          new Date(formData.validFrom) < new Date(formData.validUntil)
        );
      case 2:
        return !!(
          formData.terms.trim() &&
          (
            formData.discountType === 'promotion' || // no redemption limits needed
            formData.isUnlimited ||
            (formData.maxRedemptions && formData.maxRedemptions > 0)
          )
        );
      default:
        return true;
    }
  }, [formData]);

  const handleNext = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
      setError('');
    } else {
      setError('Please fill in all required fields correctly');
    }
  }, [activeStep, validateStep]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
    setError('');
  }, []);

  const handleBackNavigation = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate('/venue-admin-dashboard');
    }
  }, [onClose, navigate]);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(2) || !selectedVenue) {
      setError('Please complete all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const finalPrice = formData.discountType === 'fixed' && 
                        formData.originalPrice && 
                        typeof formData.discountValue === 'number'
        ? Math.max(0, formData.originalPrice - formData.discountValue)
        : undefined;

      // Create offer document first (without image)
      const offerData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        originalPrice: formData.originalPrice || null,
        finalPrice: finalPrice || null,
        validFrom: new Date(formData.validFrom + 'T00:00:00'),
        validUntil: new Date(formData.validUntil + 'T23:59:59'),
        terms: formData.terms.trim(),
        venueId: formData.venueId,
        venueName: selectedVenue.businessName || selectedVenue.name || '',
        ownerId: venueAdminData?.adminId,
        maxRedemptions: formData.isUnlimited ? null : formData.maxRedemptions,
        maxRedemptionsPerUser: formData.isUnlimitedPerUser ? null : formData.maxRedemptionsPerUser,
        currentRedemptions: 0,
        status: 'active',
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        location: {
          address: selectedVenue.address,
          latitude: (selectedVenue as any).latitude || null,
          longitude: (selectedVenue as any).longitude || null,
          geohash: null,
        },
        analytics: {
          views: 0,
          clicks: 0,
          saves: 0,
          shares: 0,
          redemptions: 0,
          clickThroughRate: 0,
          redemptionRate: 0,
          dailyStats: {},
          peakHours: [],
        },
        imageUrl: null,
        imagePath: null,
      };

      const offerRef = await addDoc(collection(db, 'offers'), offerData);
      console.log('Offer created with ID:', offerRef.id);

      // Upload image if selected
      if (selectedImage) {
        setUploadingImage(true);
        try {
          const { imageUrl, imagePath } = await uploadOfferImage(
            offerRef.id,
            formData.venueId,
            selectedImage
          );
          
          await updateDoc(doc(db, 'offers', offerRef.id), {
            imageUrl,
            imagePath,
            updatedAt: new Date(),
          });
          
          console.log('Image uploaded successfully:', imageUrl);
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          setError('Offer created but image upload failed. You can edit the offer to add an image later.');
        } finally {
          setUploadingImage(false);
        }
      }

      setSuccess('Offer created successfully!');
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating offer:', error);
      setError('Failed to create offer. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formData, selectedVenue, venueAdminData, selectedImage, onSuccess]);

  const getDiscountDisplay = () => {
    if (formData.discountType === 'percentage' && typeof formData.discountValue === 'number') {
      return `${formData.discountValue}% OFF`;
    } else if (formData.discountType === 'fixed' && typeof formData.discountValue === 'number') {
      return `$${formData.discountValue} OFF`;
    } else if (formData.discountType === 'bogo') {
      return 'BOGO';
    } else if (formData.discountType === 'free') {
      return 'FREE';
    } else if (formData.discountType === 'promotion') {
      return 'EVENT';
    }
    return '';
  };

  const calculateFinalPrice = () => {
    if (formData.discountType === 'fixed' && 
        formData.originalPrice && 
        typeof formData.discountValue === 'number') {
      return Math.max(0, formData.originalPrice - formData.discountValue);
    } else if (formData.discountType === 'percentage' && 
               formData.originalPrice && 
               typeof formData.discountValue === 'number') {
      return formData.originalPrice * (1 - formData.discountValue / 100);
    }
    return undefined;
  };

  const renderOfferDetailsStep = () => (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Basic Offer Information
        </Typography>
      </Grid>

      {/* Image Upload Section */}
      <Grid size={{ xs: 12 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            border: '2px dashed',
            borderColor: 'divider',
            backgroundColor: 'background.default',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="subtitle1" gutterBottom>
              Offer Image (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Add an eye-catching image to make your offer stand out
            </Typography>

            {imagePreview ? (
              <Box sx={{ mt: 2, position: 'relative', width: '100%', maxWidth: 400 }}>
                <img
                  src={imagePreview}
                  alt="Offer preview"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: 300,
                    objectFit: 'cover',
                    borderRadius: 8,
                  }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'error.light',
                    },
                  }}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                >
                  Choose Image
                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageSelect}
                  />
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                  JPG, PNG, GIF or WebP (Max 5MB)
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          label="Offer Title"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          required
          placeholder="e.g., 20% off all Coffee"
          helperText="Keep it short and catchy"
        />
      </Grid>
      
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          required
          placeholder="Describe what customers get with this offer"
          helperText="Provide details about the offer"
        />
      </Grid>
      
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth required>
          <InputLabel>Discount Type</InputLabel>
          <Select
            value={formData.discountType}
            onChange={(e: SelectChangeEvent) => handleDiscountTypeChange(e.target.value)}
            label="Discount Type"
          >
            <MenuItem value="percentage">
              <Box display="flex" alignItems="center">
                <Percent sx={{ mr: 1 }} />
                Percentage Off
              </Box>
            </MenuItem>
            <MenuItem value="fixed">
              <Box display="flex" alignItems="center">
                <AttachMoney sx={{ mr: 1 }} />
                Fixed Amount Off
              </Box>
            </MenuItem>
            <MenuItem value="bogo">
              <Box display="flex" alignItems="center">
                <LocalActivity sx={{ mr: 1 }} />
                Buy One Get One
              </Box>
            </MenuItem>
            <MenuItem value="free">
              <Box display="flex" alignItems="center">
                <MoneyOff sx={{ mr: 1 }} />
                Free Item/Service
              </Box>
            </MenuItem>
            <MenuItem value="promotion">
              <Box display="flex" alignItems="center">
                <LocalActivity sx={{ mr: 1 }} />
                Promotion / Event
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid size={{ xs: 12, md: 6 }}>
        {formData.discountType === 'promotion' ? (
          <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            Promotion / Event — no monetary value needed. Just describe the event in the title and description above.
          </Alert>
        ) : (formData.discountType === 'percentage' || formData.discountType === 'fixed') ? (
          <TextField
            fullWidth
            type="number"
            label={formData.discountType === 'percentage' ? 'Percentage' : 'Discount Amount'}
            value={formData.discountValue}
            onChange={(e) => handleInputChange('discountValue', Number(e.target.value))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {formData.discountType === 'percentage' ? '%' : '$'}
                </InputAdornment>
              ),
            }}
            inputProps={{ 
              min: 0,
              max: formData.discountType === 'percentage' ? 100 : undefined,
            }}
            required
            helperText={formData.discountType === 'percentage' ? 'Enter percentage (0-100)' : 'Enter dollar amount'}
          />
        ) : (
          <TextField
            fullWidth
            label="Offer Details"
            value={formData.discountValue}
            onChange={(e) => handleInputChange('discountValue', e.target.value)}
            required
            placeholder={
              formData.discountType === 'bogo' 
                ? 'e.g., Buy one coffee, get one free' 
                : 'e.g., Free dessert with main meal'
            }
            helperText="Describe what customers get"
          />
        )}
      </Grid>
      
      {formData.discountType !== 'free' && formData.discountType !== 'promotion' && (
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            type="number"
            label="Original Price (Optional)"
            value={formData.originalPrice || ''}
            onChange={(e) => handleInputChange('originalPrice', e.target.value ? Number(e.target.value) : undefined)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            helperText="The regular price before discount"
          />
        </Grid>
      )}
      
      {formData.discountType === 'fixed' && formData.originalPrice && typeof formData.discountValue === 'number' && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="info">
            Final price after discount: ${calculateFinalPrice()?.toFixed(2)}
          </Alert>
        </Grid>
      )}
    </Grid>
  );

  const renderVenueScheduleStep = () => (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Venue & Schedule
        </Typography>
      </Grid>
      
      <Grid size={{ xs: 12 }}>
        <FormControl fullWidth required>
          <InputLabel>Select Venue</InputLabel>
          <Select
            value={formData.venueId}
            onChange={(e: SelectChangeEvent) => handleVenueChange(e.target.value)}
            label="Select Venue"
          >
            {venues.map(venue => (
              <MenuItem key={venue.id} value={venue.id}>
                <Box>
                  <Typography variant="body1">{venue.businessName || venue.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {venue.address}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          type="date"
          label="Valid From"
          value={formData.validFrom}
          onChange={(e) => handleInputChange('validFrom', e.target.value)}
          required
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: toLocalDateString(new Date()) }}
          helperText="When the offer becomes available"
        />
      </Grid>
      
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          type="date"
          label="Valid Until"
          value={formData.validUntil}
          onChange={(e) => handleInputChange('validUntil', e.target.value)}
          required
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: formData.validFrom }}
          helperText="When the offer expires"
        />
      </Grid>
      
      {selectedVenue && (
        <Grid size={{ xs: 12 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Selected Venue Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Name:</strong> {selectedVenue.businessName || selectedVenue.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Address:</strong> {selectedVenue.address}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  const renderTermsLimitsStep = () => (
  <Grid container spacing={3}>
    <Grid size={{ xs: 12 }}>
      <Typography variant="h6" gutterBottom>
        Terms & Redemption Limits
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Set limits on how many times this offer can be redeemed in total and per user
      </Typography>
    </Grid>
    
    <Grid size={{ xs: 12 }}>
      <TextField
        fullWidth
        multiline
        rows={4}
        label="Terms & Conditions"
        value={formData.terms}
        onChange={(e) => handleInputChange('terms', e.target.value)}
        required
        placeholder="e.g., Valid on weekdays only. Not valid with other offers."
        helperText="Any restrictions or conditions for this offer"
      />
    </Grid>
    
    {/* Total & Per-User Redemptions — not applicable for promotions */}
    {formData.discountType !== 'promotion' && (
      <>
        {/* Total Redemptions Section */}
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Total Redemption Limit
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Limit how many times this offer can be redeemed across ALL users
          </Typography>
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isUnlimited}
                onChange={(e) => handleInputChange('isUnlimited', e.target.checked)}
              />
            }
            label="Unlimited total redemptions"
          />
        </Grid>
        
        {!formData.isUnlimited && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Maximum Total Redemptions"
              value={formData.maxRedemptions || ''}
              onChange={(e) => handleInputChange('maxRedemptions', e.target.value ? Number(e.target.value) : undefined)}
              inputProps={{ min: 1 }}
              required={!formData.isUnlimited}
              helperText="Total times this offer can be redeemed (e.g., 100)"
              InputProps={{
                startAdornment: <InputAdornment position="start">👥</InputAdornment>,
              }}
            />
          </Grid>
        )}
        
        {/* Per-User Redemptions Section */}
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Per-User Redemption Limit
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Limit how many times EACH individual user can redeem this offer
          </Typography>
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isUnlimitedPerUser}
                onChange={(e) => handleInputChange('isUnlimitedPerUser', e.target.checked)}
              />
            }
            label="Unlimited redemptions per user"
          />
        </Grid>
        
        {!formData.isUnlimitedPerUser && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Maximum Redemptions Per User"
              value={formData.maxRedemptionsPerUser || ''}
              onChange={(e) => handleInputChange('maxRedemptionsPerUser', e.target.value ? Number(e.target.value) : undefined)}
              inputProps={{ min: 1 }}
              required={!formData.isUnlimitedPerUser}
              helperText="Times each user can redeem (e.g., 1 for one-time use)"
              InputProps={{
                startAdornment: <InputAdornment position="start">👤</InputAdornment>,
              }}
            />
          </Grid>
        )}
        
        {/* Example scenarios */}
        {(!formData.isUnlimited || !formData.isUnlimitedPerUser) && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" component="div">
                <strong>Example:</strong>
                {!formData.isUnlimited && !formData.isUnlimitedPerUser && formData.maxRedemptions && formData.maxRedemptionsPerUser && (
                  <> {formData.maxRedemptions} total redemptions, {formData.maxRedemptionsPerUser} per user means up to {Math.floor(formData.maxRedemptions / formData.maxRedemptionsPerUser)} users can redeem this offer.</>
                )}
                {formData.isUnlimited && !formData.isUnlimitedPerUser && formData.maxRedemptionsPerUser && (
                  <> Unlimited total, but each user can only redeem {formData.maxRedemptionsPerUser} time{formData.maxRedemptionsPerUser > 1 ? 's' : ''}.</>
                )}
                {!formData.isUnlimited && formData.isUnlimitedPerUser && formData.maxRedemptions && (
                  <> {formData.maxRedemptions} total redemptions, but individual users can redeem multiple times until the total is reached.</>
                )}
              </Typography>
            </Alert>
          </Grid>
        )}
      </>
    )}
  </Grid>
);

  const renderPreviewStep = () => (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" gutterBottom>
          Preview Your Offer
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          This is how your offer will appear to customers
        </Typography>
      </Grid>
      
      <Grid size={{ xs: 12, md: 8 }}>
        <Card variant="outlined" sx={{ position: 'relative' }}>
          {/* Offer Image Preview */}
          {imagePreview && (
            <Box
              component="img"
              src={imagePreview}
              alt="Offer"
              sx={{
                width: '100%',
                height: 200,
                objectFit: 'cover',
              }}
            />
          )}
          
          <CardContent>
            {/* Offer Badge */}
            <Box position="absolute" top={imagePreview ? 176 : 16} right={16}>
              <Chip 
                label={getDiscountDisplay()} 
                color="secondary" 
                size="medium"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
            
            <Typography variant="h5" gutterBottom sx={{ pr: 8 }}>
              {formData.title}
            </Typography>
            
            <Typography variant="body1" paragraph>
              {formData.description}
            </Typography>
            
            <Box display="flex" alignItems="center" mb={2}>
              <Business sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {selectedVenue?.businessName || selectedVenue?.name}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" mb={2}>
              <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Valid from {new Date(formData.validFrom).toLocaleDateString()} to {new Date(formData.validUntil).toLocaleDateString()}
              </Typography>
            </Box>
            
            {formData.originalPrice && (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Original price: <span style={{ textDecoration: 'line-through' }}>${formData.originalPrice}</span>
                </Typography>
                {calculateFinalPrice() !== undefined && (
                  <Typography variant="h6" color="primary">
                    Final price: ${calculateFinalPrice()?.toFixed(2)}
                  </Typography>
                )}
              </Box>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="text.secondary">
              <strong>Terms:</strong> {formData.terms}
            </Typography>
            
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {formData.isUnlimited ? 'Unlimited redemptions' : `Limited to ${formData.maxRedemptions} redemptions`}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
    {formData.isUnlimitedPerUser ? '∞ Unlimited per user' : `👤 ${formData.maxRedemptionsPerUser} per user`}
  </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid size={{ xs: 12, md: 4 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Offer Summary
            </Typography>
            
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Image</Typography>
              <Typography variant="body1">
                {selectedImage ? '✓ Image attached' : 'No image'}
              </Typography>
            </Box>
            
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Discount Type</Typography>
              <Typography variant="body1">{formData.discountType.toUpperCase()}</Typography>
            </Box>
            
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Venue</Typography>
              <Typography variant="body1">{selectedVenue?.businessName || selectedVenue?.name}</Typography>
            </Box>
            
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Duration</Typography>
              <Typography variant="body1">
                {Math.ceil((new Date(formData.validUntil).getTime() - new Date(formData.validFrom).getTime()) / (1000 * 60 * 60 * 24))} days
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">Redemptions</Typography>
              <Typography variant="body1">
                {formData.isUnlimited ? 'Unlimited' : formData.maxRedemptions}
              </Typography>
            </Box>
            <Box>
  <Typography variant="body2" color="text.secondary">Per User</Typography>
  <Typography variant="body1">
    {formData.isUnlimitedPerUser ? 'Unlimited' : formData.maxRedemptionsPerUser}
  </Typography>
</Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: return renderOfferDetailsStep();
      case 1: return renderVenueScheduleStep();
      case 2: return renderTermsLimitsStep();
      case 3: return renderPreviewStep();
      default: return null;
    }
  };

  if (success) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, mb: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 6 }}>
              <LocalOffer color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h4" color="success.main" gutterBottom>
                Offer Created Successfully!
              </Typography>
              <Typography variant="body1" paragraph>
                Your offer is now live and visible to customers in your area.
              </Typography>
              {uploadingImage && (
                <Box sx={{ mt: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Uploading image...
                  </Typography>
                </Box>
              )}
              <Button variant="contained" onClick={handleBackNavigation} sx={{ mt: 2 }}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleBackNavigation}>
            <ArrowBack />
          </IconButton>
          <LocalOffer sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Create New Offer
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {venueAdminData?.subscription?.totalOffersUsed >= venueAdminData?.subscription?.totalOffersLimit && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Offer Limit Reached:</strong> You've used all your available offers for this month. 
                Upgrade your plan to create more offers.
              </Alert>
            )}

            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button disabled={activeStep === 0} onClick={handleBack}>
                Back
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !validateStep(2)}
                  startIcon={loading || uploadingImage ? <CircularProgress size={20} /> : <Publish />}
                >
                  {loading ? 'Creating Offer...' : uploadingImage ? 'Uploading Image...' : 'Publish Offer'}
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  onClick={handleNext}
                  disabled={!validateStep(activeStep)}
                >
                  Next
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CreateOffer;